'use client'

import { Divider, Text } from '@macarongfactory/web-ui'
import { Fragment, useState } from 'react'

import { getCarHubImageUrl } from '@/entities/car'

import SectionHeading from './SectionHeading'
import {
  isAlreadyBrokenImage,
  MODOOCAR_CAR_FALLBACK_IMAGE_URL,
  MODOOCAR_CODE,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type {
  ICompareItem,
  ICompareMetricValue,
  IModoocarDetail,
} from '../_features/modoocar'

interface ICarCompareSectionProps {
  detail: IModoocarDetail
  asSubsection?: boolean
}

type TCompareRow =
  | {
      key: 'efficiency' | 'fuelCost' | 'ownerCount'
      label: string
      kind: 'metric'
    }
  | { key: 'satisfaction'; label: string; kind: 'plain' }

const COMPARE_ROWS: TCompareRow[] = [
  { key: 'efficiency', label: '연비', kind: 'metric' },
  { key: 'fuelCost', label: '유류비', kind: 'metric' },
  { key: 'satisfaction', label: '만족도', kind: 'plain' },
  { key: 'ownerCount', label: '오너수', kind: 'metric' },
]

const HEADER_HEIGHT = 'h-[90px]'
const CAR_IMAGE_BOX = 'h-[57px] w-[100px]'
const ROW_HEIGHT = 'h-[26px]'
const COLUMN_WIDTH = 'w-[132px]'
const MAX_COMPARE_ITEMS = 4
const LABEL_COLUMN_WIDTH = 'w-11'

const CompareCarImage = ({
  imageUrls,
  modelName,
}: {
  imageUrls: string[]
  modelName: string
}) => {
  const [srcIndex, setSrcIndex] = useState(0)
  // [주의]: 실패한 후보에서만 다음 후보로 넘깁니다. ref 콜백(StrictMode에서 두 번 호출)과 onError가 겹쳐 불리면 후보를 건너뛰게 됩니다.
  const skipFailedImage = () =>
    setSrcIndex(prev => (prev === srcIndex ? prev + 1 : prev))
  const currentUrl = [...imageUrls, MODOOCAR_CAR_FALLBACK_IMAGE_URL][srcIndex]

  if (!currentUrl) {
    return (
      <div className={`flex items-center justify-center ${CAR_IMAGE_BOX}`}>
        <Text
          variant='caption-1-medium'
          color='text-foreground-neutral-assistive'
        >
          {modelName}
        </Text>
      </div>
    )
  }

  return (
    <img
      key={currentUrl}
      src={currentUrl}
      alt={modelName}
      className={`object-contain ${CAR_IMAGE_BOX}`}
      ref={node => {
        if (isAlreadyBrokenImage(node)) skipFailedImage()
      }}
      onError={skipFailedImage}
    />
  )
}

const MetricValue = ({
  metric,
  current,
}: {
  metric: ICompareMetricValue
  current: boolean
}) => {
  const valueColor = current
    ? 'text-foreground-brand-strong'
    : 'text-foreground-neutral-strong'

  return (
    <span className='inline-flex items-baseline gap-dimension-x0-5'>
      <Text as='span' variant='title-1-semibold' color={valueColor}>
        {metric.value}
      </Text>
      <Text as='span' variant='title-2-medium' color={valueColor}>
        {metric.unit}
      </Text>
    </span>
  )
}

const PlainValue = ({ text, current }: { text: string; current: boolean }) => (
  <Text
    variant='title-1-semibold'
    color={
      current
        ? 'text-foreground-brand-strong'
        : 'text-foreground-neutral-strong'
    }
  >
    {text}
  </Text>
)

const CompareValueRow = ({
  row,
  item,
}: {
  row: TCompareRow
  item: ICompareItem
}) => (
  <div className={`flex items-center justify-center ${ROW_HEIGHT}`}>
    {row.kind === 'metric' ? (
      <MetricValue metric={item[row.key]} current={!!item.current} />
    ) : (
      <PlainValue text={item[row.key]} current={!!item.current} />
    )}
  </div>
)

const CarCompareSection = ({
  detail,
  asSubsection = false,
}: ICarCompareSectionProps) => {
  const { segment, compare, brand, lowerModelName } = detail
  const visibleCompare = compare.slice(0, MAX_COMPARE_ITEMS)

  const { sectionShow, itemClick } = useModoocarTracking()
  const sectionRef = useModoocarImpression<HTMLElement>(() =>
    sectionShow(MODOOCAR_CODE.MODEL_COMPARE, '차종 비교')
  )
  const compareRanks = new Map<string, number>()
  let nextRank = 1
  visibleCompare.forEach(item => {
    if (!item.current) {
      compareRanks.set(item.modelName, nextRank)
      nextRank += 1
    }
  })
  const handleCardClick = (item: ICompareItem) => {
    if (item.current) return
    itemClick(
      MODOOCAR_CODE.MODEL_COMPARE,
      'card',
      `차종 비교 ${item.modelName}`,
      {
        rank: compareRanks.get(item.modelName) ?? 1,
      }
    )
    if (!item.brand || typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.search = new URLSearchParams({
      brandName: item.brand,
      modelName: item.modelName,
    }).toString()
    window.location.href = url.toString()
  }

  // [정책]: 차종 이미지는 세부모델(세대명)로만 만들고 상위모델 이미지로 대신하지 않습니다. 실패하면 폴백 이미지를 보여 줍니다.
  const currentCarImageUrls = [getCarHubImageUrl(brand, lowerModelName)]

  return (
    <section
      ref={sectionRef}
      id={asSubsection ? undefined : 'compare'}
      className={
        asSubsection
          ? 'flex flex-col gap-dimension-x4 pb-dimension-x8'
          : 'flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 py-dimension-x8 lg:scroll-mt-[112px]'
      }
    >
      <div className='px-dimension-x5'>
        {asSubsection ? (
          <Text
            as='h3'
            variant='title-2-semibold'
            color='text-foreground-neutral-normal'
          >
            {`${segment} 차종 비교`}
          </Text>
        ) : (
          <SectionHeading
            title={`${segment} 차종 비교`}
            titleVariant='title-1-semibold'
          />
        )}
      </div>

      <div className='scrollbar-hide flex gap-dimension-x2 overflow-x-auto px-dimension-x5'>
        <div
          className={`flex shrink-0 flex-col gap-dimension-x8 pb-dimension-x6 pt-dimension-x4 ${LABEL_COLUMN_WIDTH}`}
        >
          <div className={HEADER_HEIGHT} aria-hidden />
          <div className='flex flex-col gap-dimension-x4'>
            {COMPARE_ROWS.map((row, index) => (
              <Fragment key={row.key}>
                <div className={`flex items-center ${ROW_HEIGHT}`}>
                  <Text
                    variant='body-1-medium'
                    color='text-foreground-neutral-alternative'
                  >
                    {row.label}
                  </Text>
                </div>
                {index < COMPARE_ROWS.length - 1 ? (
                  <div
                    aria-hidden
                    className='h-px w-full bg-background-surface-default'
                  />
                ) : null}
              </Fragment>
            ))}
          </div>
        </div>

        {visibleCompare.map(item => (
          <div
            key={item.modelName}
            role={item.current ? undefined : 'button'}
            tabIndex={item.current ? undefined : 0}
            onClick={item.current ? undefined : () => handleCardClick(item)}
            className={`flex shrink-0 flex-col gap-dimension-x8 rounded-dimension-x4 px-dimension-x4 pb-dimension-x6 pt-dimension-x4 ${COLUMN_WIDTH} ${
              item.current
                ? 'bg-background-brand-weak'
                : 'cursor-pointer bg-background-neutral-weak'
            }`}
          >
            <div
              className={`flex flex-col items-center justify-center gap-dimension-x2-5 ${HEADER_HEIGHT}`}
            >
              <Text
                variant='title-2-semibold'
                color={
                  item.current
                    ? 'text-foreground-brand-strong'
                    : 'text-foreground-neutral-strong'
                }
                className='line-clamp-1 w-full text-center'
              >
                {item.modelName}
              </Text>
              <CompareCarImage
                imageUrls={item.current ? currentCarImageUrls : [item.imageUrl]}
                modelName={item.modelName}
              />
            </div>

            <div className='flex flex-col gap-dimension-x4'>
              {COMPARE_ROWS.map((row, index) => (
                <Fragment key={row.key}>
                  <CompareValueRow row={row} item={item} />
                  {index < COMPARE_ROWS.length - 1 ? (
                    <Divider.Horizontal />
                  ) : null}
                </Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default CarCompareSection
